import { useState } from 'react'
import { useMutation } from 'urql'
import { gql } from '../gql/gql'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import { SUPPORTED_CURRENCIES } from '../lib/currencies'
import { validateName } from '../lib/utils'

const UPDATE_USER_MUTATION = gql(`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      name
      email
      preferredCurrency
    }
  }
`)

interface EditProfileDialogProps {
  currentName: string
  currentEmail: string
  currentCurrency?: string | null
  children?: React.ReactNode
}

export function EditProfileDialog({ currentName, currentEmail, currentCurrency, children }: EditProfileDialogProps) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(currentName)
  const [currency, setCurrency] = useState(currentCurrency || 'USD')
  const [error, setError] = useState('')

  const [{ fetching }, updateUser] = useMutation(UPDATE_USER_MUTATION)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const nameError = validateName(name)
    if (nameError) { setError(nameError); return }

    const result = await updateUser({ input: { name, preferredCurrency: currency } })

    if (result.error) {
      setError(result.error.message)
      return
    }

    setOpen(false)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setName(currentName)
      setCurrency(currentCurrency || 'USD')
      setError('')
    }
    setOpen(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription className="text-xs">{currentEmail}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="space-y-1">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger id="currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map(({ code, label }) => (
                  <SelectItem key={code} value={code}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={fetching}>
            {fetching ? 'Saving...' : 'Save'}
          </Button>
        </form>

        <div className="border-t pt-3">
          <p className="text-xs font-medium text-foreground mb-1">Receipt Forwarding</p>
          <p className="text-xs text-muted-foreground mb-2">
            Forward any order confirmation email to this address and it logs automatically.
          </p>
          <div className="flex items-center justify-between bg-muted rounded px-3 py-2">
            <span className="text-xs font-mono">receipts@getbuylog.com</span>
            <button
              type="button"
              className="text-xs text-primary font-medium ml-3 hover:underline"
              onClick={() => navigator.clipboard.writeText('receipts@getbuylog.com')}
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Send from {currentEmail}</p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
